/**
 * /network/me — Profile editor · Full-width two-column layout
 * Left: edit form   Right: live preview + suggestions + prefs
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Save, Eye, EyeOff, Pin, PinOff, Loader2, Star, Globe, Youtube,
  Instagram, Inbox, Sparkles, Zap, Trophy, Network, Check, Users,
  SlidersHorizontal, ChevronRight, MessageCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import api from '@/lib/api';
import { leftTopArrowGradient } from '@/assets/svgs/gradientIcons';

/* ─── constants ─────────────────────────────────────────── */
const ROLES = [
  { id: '', label: '— Select Role —' },
  { id: 'influencer', label: 'Influencer' },
  { id: 'creator', label: 'Creator' },
  { id: 'storyteller', label: 'Storyteller' },
  { id: 'ai_moviemaker', label: 'AI Moviemaker' },
  { id: 'ad_maker', label: 'Ad Maker' },
  { id: 'podcaster', label: 'Podcaster' },
  { id: 'video_editor', label: 'Video Editor' },
];

const LEVELS = [
  { num:1, label:'Newcomer', min:0 }, { num:2, label:'Maker', min:100 },
  { num:3, label:'Creator', min:300 }, { num:4, label:'Featured', min:700 },
  { num:5, label:'Influencer', min:1500 }, { num:6, label:'Studio Lead', min:3000 },
];

const BADGE_LABELS = {
  joined:'🌐 Joined', first_pin:'📌 First Pin', showcase:'🎨 Showcase',
  portfolio:'🔗 Portfolio', storyteller:'📖 Storyteller', producer:'🎬 Producer',
  first_reply:'💬 First Reply', veteran:'⭐ Veteran',
};

const LV_COL = ['#94a3b8','#7dd3fc','#c4b5fd','#67e8f9','#f0abfc','#62FAE3'];
const lvCol  = lv => LV_COL[Math.min((lv||1)-1,5)];

function deriveHandle(name, email) {
  const clean = s => (s||'').toLowerCase().replace(/[^a-z0-9_]/g,'_').replace(/_+/g,'_').replace(/^_+|_+$/g,'').slice(0,24);
  const fromName  = clean(name);
  const fromEmail = clean((email||'').split('@')[0]);
  const base      = fromName.length >= 3 ? fromName : fromEmail;
  return base.length >= 3 ? base : (base + 'creator').slice(0,24);
}

/* ─── sub-components ─────────────────────────────────────── */
function XpBar({ xp, nextLevelXp, nextLabel }) {
  const pct = nextLevelXp ? Math.min(100, Math.round((xp / nextLevelXp) * 100)) : 100;
  return (
    <div className="space-y-1.5">
      <div className="flex justify-between text-[11px]">
        <span className="text-[#62FAE3] font-bold">{(xp||0).toLocaleString()} XP</span>
        <span className="text-[#8a94a6]">{nextLevelXp ? `→ ${nextLevelXp} XP · ${nextLabel}` : '✨ Max level!'}</span>
      </div>
      <div className="h-2 rounded-full bg-white/[0.08] overflow-hidden">
        <motion.div initial={{ width:0 }} animate={{ width:`${pct}%` }} transition={{ duration:0.9, ease:'easeOut' }}
          className="h-full rounded-full"
          style={{ background:'linear-gradient(90deg,#D0BCFF,#62FAE3)', boxShadow:'0 0 6px rgba(98,250,227,0.45)' }} />
      </div>
    </div>
  );
}

function ProfilePreview({ displayName, handle, bio, role, avatarUrl, progress }) {
  const lv  = progress?.level || 1;
  const col = lvCol(lv);
  const init = (displayName || handle || '?')[0]?.toUpperCase();
  return (
    <div className="rounded-2xl border border-white/[0.08] bg-white/[0.03] overflow-hidden">
      <div className="h-16 relative" style={{ background:`linear-gradient(135deg, ${col}20, transparent)` }}>
        <div className="absolute inset-0 opacity-30"
          style={{ background:`radial-gradient(ellipse at 20% 50%, ${col} 0%, transparent 70%)` }} />
      </div>
      <div className="px-4 pb-4">
        <div className="-mt-5 flex items-end gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl border-2 border-[#0b0e13] flex items-center justify-center font-black text-sm overflow-hidden flex-shrink-0"
            style={{ background:`linear-gradient(135deg, ${col}cc, ${col}44)`, color:'#080d14' }}>
            {avatarUrl ? <img src={avatarUrl} alt="" className="w-full h-full object-cover" /> : init}
          </div>
          <div className="mb-1 flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border"
            style={{ color:col, borderColor:`${col}35`, background:`${col}12` }}>
            <Star className="w-2.5 h-2.5" /> Lv {lv}
          </div>
        </div>
        <p className="font-bold text-white text-sm">{displayName || 'Your Name'}</p>
        <p className="text-[11px] text-[#8a94a6]">@{handle || 'yourhandle'}</p>
        {role && <span className="inline-block mt-1 text-[10px] px-2 py-0.5 rounded-full bg-[#D0BCFF]/10 text-[#D0BCFF] border border-[#D0BCFF]/20">{role}</span>}
        {bio && <p className="text-[11px] text-[#8a94a6] mt-2 line-clamp-2 leading-relaxed">{bio}</p>}
      </div>
    </div>
  );
}

function SuggestCard({ profile, onClick }) {
  const lv = profile.progress?.level || 1;
  const col = lvCol(lv);
  return (
    <div onClick={onClick} className="group cursor-pointer flex items-center gap-2.5 p-2.5 rounded-xl hover:bg-white/[0.05] transition-all">
      <div className="w-8 h-8 rounded-lg flex items-center justify-center font-black text-xs flex-shrink-0 overflow-hidden"
        style={{ background:`linear-gradient(135deg, ${col}cc, ${col}44)`, color:'#080d14' }}>
        {profile.avatar_url
          ? <img src={profile.avatar_url} alt="" className="w-full h-full object-cover" />
          : (profile.display_name||'?')[0]?.toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold text-white truncate">{profile.display_name}</p>
        <p className="text-[10px] text-[#8a94a6]">@{profile.handle}</p>
      </div>
      <ChevronRight className="w-3 h-3 text-[#8a94a6] group-hover:text-[#62FAE3] transition-colors" />
    </div>
  );
}

/* ─── main page ──────────────────────────────────────────── */
export const NetworkProfileEditorPage = () => {
  const navigate = useNavigate();
  const [profile, setProfile]           = useState(null);
  const [progress, setProgress]         = useState(null);
  const [works, setWorks]               = useState([]);
  const [suggestions, setSuggestions]   = useState([]);
  const [loading, setLoading]           = useState(true);
  const [saving, setSaving]             = useState(false);
  const [publishing, setPublishing]     = useState(false);
  const [autoCreated, setAutoCreated]   = useState(false);

  const [handle, setHandle]           = useState('');
  const [displayName, setDisplayName] = useState('');
  const [bio, setBio]                 = useState('');
  const [role, setRole]               = useState('');
  const [avatarUrl, setAvatarUrl]     = useState('');
  const [links, setLinks]             = useState({ youtube:'', instagram:'', portfolio:'' });
  const [accepting, setAccepting]     = useState(true);
  const [showLinks, setShowLinks]     = useState(false);
  const [pins, setPins]               = useState([]);
  const [pinTab, setPinTab]           = useState('works');    // 'works' | 'pins'

  const applyProfile = p => {
    setProfile(p); setHandle(p.handle||''); setDisplayName(p.display_name||'');
    setBio(p.bio||''); setRole(p.role||''); setAvatarUrl(p.avatar_url||'');
    setLinks({ youtube:'', instagram:'', portfolio:'', ...(p.links||{}) });
    setAccepting(p.accepting_messages !== false); setPins(p.pins||[]);
  };

  const load = useCallback(async () => {
    try {
      const [netRes, worksRes, authRes, dirRes] = await Promise.all([
        api.get('/network/me'),
        api.get('/network/works'),
        api.get('/auth/me'),
        api.get('/network?limit=10').catch(() => ({ data:{ profiles:[] } })),
      ]);
      const { profile:p, progress:pg } = netRes.data;
      setProgress(pg); setWorks(worksRes.data.works||[]);
      setSuggestions((dirRes.data.profiles||[]).slice(0,5));
      if (p) { applyProfile(p); return; }

      const u = authRes.data?.user || authRes.data || {};
      const dh = deriveHandle(u.name, u.email);
      try {
        const r = await api.put('/network/me', { handle:dh, display_name:u.name||dh, bio:'', accepting_messages:true });
        applyProfile(r.data.profile); setProgress(r.data.progress); setAutoCreated(true);
      } catch (e) {
        if (e?.response?.status === 409) {
          const alt = (dh.slice(0,21) + '_' + Math.floor(Math.random()*900+100)).slice(0,24);
          try {
            const r2 = await api.put('/network/me', { handle:alt, display_name:u.name||alt, bio:'', accepting_messages:true });
            applyProfile(r2.data.profile); setProgress(r2.data.progress); setAutoCreated(true);
          } catch { setHandle(alt); setDisplayName(u.name||''); }
        } else { setHandle(dh); setDisplayName(u.name||''); }
      }
    } catch { toast.error('Failed to load profile'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (autoCreated) toast.success('✨ Profile created — customize and publish!'); }, [autoCreated]);

  const handleSave = async () => {
    if (!handle) return toast.error('Handle required');
    if (!displayName) return toast.error('Display name required');
    setSaving(true);
    try {
      const fl = Object.fromEntries(Object.entries(links).filter(([,v]) => v?.startsWith('https://')));
      const { data } = await api.put('/network/me', { handle, display_name:displayName, bio, role:role||undefined, avatar_url:avatarUrl||undefined, links:fl, accepting_messages:accepting });
      applyProfile(data.profile); setProgress(data.progress); toast.success('Profile saved!');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const savePins = async nextPins => {
    try {
      const { data } = await api.put('/network/me/pins', { pins: nextPins.map(p => ({ source_id:p.source_id||p.id, source_type:p.source_type||'story', title:p.title, preview_url:p.preview_url||null })) });
      setPins(data.pins||nextPins); setProgress(data.progress); toast.success('Pins updated');
    } catch (err) { toast.error(err?.response?.data?.detail||'Pin update failed'); }
  };

  const togglePin = work => {
    const already = pins.find(p => p.source_id === work.id);
    if (already) savePins(pins.filter(p => p.source_id !== work.id));
    else { if (pins.length >= 12) return toast.error('Max 12 pins'); savePins([...pins, { source_id:work.id, source_type:work.source_type||'story', title:work.title }]); }
  };

  const handlePublish = async () => {
    setPublishing(true);
    try { await api.post('/network/me/publish'); toast.success('🎉 Profile is live!'); load(); }
    catch (err) { toast.error(err?.response?.data?.detail||'Publish failed'); }
    finally { setPublishing(false); }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-3">
        <Loader2 className="w-8 h-8 animate-spin text-[#62FAE3]" />
        <p className="text-[#8a94a6] text-sm">Setting up your creator profile…</p>
      </div>
    );
  }

  const nextLevel  = LEVELS.find(l => l.num === (progress?.level||1) + 1);
  const col        = lvCol(progress?.level||1);

  return (
    <div className="min-h-screen bg-transparent text-white relative overflow-x-hidden">
      <div className="fixed top-0 left-0 w-[700px] pointer-events-none z-0 opacity-35"
        style={{ transform:'translate(-20%,-20%)' }} dangerouslySetInnerHTML={{ __html: leftTopArrowGradient }} />

      {/* ── Header ── */}
      <div className="relative px-5 sm:px-8 pt-6 pb-4 border-b border-white/[0.06]"
        style={{ background:'linear-gradient(180deg,rgba(208,188,255,0.04) 0%,transparent 100%)' }}>
        <div className="flex items-center justify-between flex-wrap gap-3 relative z-10">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center border border-[#D0BCFF]/20"
                style={{ background:'rgba(208,188,255,0.08)' }}>
                <Network className="w-3.5 h-3.5 text-[#D0BCFF]" />
              </div>
              <span className="text-[11px] font-bold tracking-widest text-[#D0BCFF] uppercase">Creator Network</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-black tracking-tight">
              <span style={{ background:'linear-gradient(106deg,#D0BCFF 0%,#62FAE3 100%)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>
                My Creator Profile
              </span>
            </h1>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={() => navigate('/network/inbox')}
              className="border-white/10 bg-white/[0.04] text-[#c5cbd6] hover:text-white hover:bg-white/[0.08]">
              <Inbox className="w-4 h-4 mr-1" /> Inbox
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/network')}
              className="border-white/10 bg-white/[0.04] text-[#c5cbd6] hover:text-white hover:bg-white/[0.08]">
              <Users className="w-4 h-4 mr-1" /> Directory
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/network/leaderboard')}
              className="border-white/10 bg-white/[0.04] text-amber-400 hover:text-amber-300 hover:bg-white/[0.08] hover:border-amber-500/30">
              <Trophy className="w-4 h-4 mr-1" /> Board
            </Button>
          </div>
        </div>
      </div>

      {/* ── Two-column body ── */}
      <div className="flex gap-0 relative z-10">
        {/* LEFT: edit form */}
        <div className="flex-1 min-w-0 p-5 sm:p-8 space-y-5">
          {/* Welcome banner */}
          <AnimatePresence>
            {autoCreated && (
              <motion.div initial={{ opacity:0, y:-8 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0 }}
                className="flex items-start gap-3 p-4 rounded-2xl border border-[#62FAE3]/20 bg-[#62FAE3]/5">
                <Sparkles className="w-5 h-5 text-[#62FAE3] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold text-white text-sm">Welcome to the Creator Network! 🎉</p>
                  <p className="text-xs text-[#8a94a6] mt-0.5">Starter profile created from your account. Customize it and hit <strong className="text-white">Publish</strong>.</p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Progress bar */}
          {progress && (
            <div className="p-4 rounded-2xl border border-white/[0.08] bg-white/[0.03]">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-xl flex items-center justify-center"
                    style={{ background:`${col}18`, border:`1px solid ${col}35` }}>
                    <Star className="w-4 h-4" style={{ color:col }} />
                  </div>
                  <div>
                    <p className="font-black text-sm" style={{ color:col }}>Level {progress.level} — {progress.level_label}</p>
                    <p className="text-[10px] text-[#8a94a6]">{(progress.xp||0).toLocaleString()} XP total</p>
                  </div>
                </div>
              </div>
              <XpBar xp={progress.xp||0} nextLevelXp={progress.next_level_xp} nextLabel={nextLevel?.label} />
              {(progress.badges||[]).length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t border-white/[0.07]">
                  {progress.badges.map(b => (
                    <span key={b.id} className="text-[11px] px-2 py-0.5 rounded-full border border-[#62FAE3]/20 bg-[#62FAE3]/8 text-[#62FAE3]">
                      {BADGE_LABELS[b.id]||b.id}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Publish strip */}
          <div className={`rounded-2xl border p-4 flex items-center justify-between gap-4 ${profile?.published ? 'border-[#62FAE3]/20 bg-[#62FAE3]/4' : 'border-white/[0.08] bg-white/[0.03]'}`}>
            <div>
              <p className="font-bold text-white text-sm">{profile?.published ? '🟢 Live on the Network' : '⚫ Not Published'}</p>
              <p className="text-xs text-[#8a94a6] mt-0.5">{profile?.published ? 'Visible in directory and leaderboard.' : 'Publish to appear in the directory.'}</p>
            </div>
            {profile?.published ? (
              <Button size="sm" variant="outline" onClick={async () => { try { await api.post('/network/me/unpublish'); toast.success('Hidden'); load(); } catch { toast.error('Failed'); } }}
                className="border-white/10 bg-white/[0.04] text-[#8a94a6] hover:text-white flex-shrink-0">
                <EyeOff className="w-4 h-4 mr-1" /> Unpublish
              </Button>
            ) : (
              <Button size="sm" onClick={handlePublish} disabled={publishing}
                className="font-bold text-[#080d14] flex-shrink-0"
                style={{ background:'linear-gradient(106.58deg,#D0BCFF 0%,#62FAE3 100%)', boxShadow:'0 0 14px rgba(98,250,227,0.25)' }}>
                {publishing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
                Publish
              </Button>
            )}
          </div>

          {/* Form */}
          <div className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-5 space-y-4">
            <p className="font-bold text-white text-sm flex items-center gap-2">
              <span className="w-1 h-4 rounded-full" style={{ background:'linear-gradient(to bottom,#D0BCFF,#62FAE3)' }} />
              Profile Info
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-[11px] text-[#8a94a6] font-medium">Handle *</label>
                <Input value={handle} onChange={e => setHandle(e.target.value.toLowerCase())} placeholder="your_handle"
                  className="bg-white/[0.04] border-white/10 text-white placeholder-[#8a94a6] focus:border-[#62FAE3]/40" />
              </div>
              <div className="space-y-1.5">
                <label className="text-[11px] text-[#8a94a6] font-medium">Display Name *</label>
                <Input value={displayName} onChange={e => setDisplayName(e.target.value)} placeholder="Your Name"
                  className="bg-white/[0.04] border-white/10 text-white placeholder-[#8a94a6] focus:border-[#62FAE3]/40" />
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-[11px] text-[#8a94a6] font-medium">Bio</label>
              <textarea value={bio} onChange={e => setBio(e.target.value)} maxLength={500} rows={3}
                placeholder="Tell the world what you create…"
                className="w-full bg-white/[0.04] border border-white/10 text-white placeholder-[#8a94a6] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#62FAE3]/40 transition-colors resize-none" />
              <p className="text-[10px] text-[#8a94a6] text-right">{bio.length}/500</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-[11px] text-[#8a94a6] font-medium">Role</label>
                <select value={role} onChange={e => setRole(e.target.value)}
                  className="w-full bg-white/[0.04] border border-white/10 text-white rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#62FAE3]/40 appearance-none">
                  {ROLES.map(r => <option key={r.id} value={r.id} className="bg-[#0b0e13]">{r.label}</option>)}
                </select>
              </div>
              <div className="space-y-1.5">
                <label className="text-[11px] text-[#8a94a6] font-medium">Avatar URL</label>
                <Input value={avatarUrl} onChange={e => setAvatarUrl(e.target.value)} placeholder="https://…"
                  className="bg-white/[0.04] border-white/10 text-white placeholder-[#8a94a6] focus:border-[#62FAE3]/40" />
              </div>
            </div>

            {/* Links accordion */}
            <button type="button" onClick={() => setShowLinks(v=>!v)}
              className="flex items-center gap-2 text-xs text-[#8a94a6] hover:text-[#62FAE3] transition-colors">
              <Globe className="w-3.5 h-3.5" />
              External Links (optional)
              <span className={`ml-1 transition-transform ${showLinks ? 'rotate-180' : ''}`}>▾</span>
            </button>
            <AnimatePresence>
              {showLinks && (
                <motion.div initial={{ height:0, opacity:0 }} animate={{ height:'auto', opacity:1 }} exit={{ height:0, opacity:0 }}
                  className="overflow-hidden space-y-2">
                  {[{key:'youtube',Icon:Youtube,ph:'https://youtube.com/@you'},{key:'instagram',Icon:Instagram,ph:'https://instagram.com/you'},{key:'portfolio',Icon:Globe,ph:'https://yoursite.com'}].map(({key,Icon,ph}) => (
                    <div key={key} className="flex items-center gap-2">
                      <Icon className="w-4 h-4 text-[#8a94a6] flex-shrink-0" />
                      <Input value={links[key]||''} onChange={e => setLinks(l=>({...l,[key]:e.target.value}))} placeholder={ph}
                        className="bg-white/[0.04] border-white/10 text-white placeholder-[#8a94a6] focus:border-[#62FAE3]/40 text-sm" />
                    </div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Accept messages toggle */}
            <div className="flex items-center justify-between py-2 border-t border-white/[0.07]">
              <div>
                <p className="text-sm text-white font-medium">Accept message requests</p>
                <p className="text-[11px] text-[#8a94a6]">Let others send you a DM request</p>
              </div>
              <button type="button" onClick={() => setAccepting(v=>!v)}
                className={`relative w-11 h-6 rounded-full transition-all duration-300 ${accepting ? '' : 'bg-white/20'}`}
                style={accepting ? { background:'linear-gradient(90deg,#D0BCFF,#62FAE3)' } : undefined}>
                <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all duration-300 ${accepting ? 'left-[22px]' : 'left-0.5'}`} />
              </button>
            </div>

            <Button onClick={handleSave} disabled={saving} className="w-full font-bold text-[#080d14]"
              style={{ background:'linear-gradient(106.58deg,#D0BCFF 0%,#62FAE3 100%)', boxShadow:'0 0 16px rgba(98,250,227,0.2)' }}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Save Profile
            </Button>
          </div>

          {/* Pin picker */}
          <div className="rounded-2xl border border-white/[0.08] bg-white/[0.03] overflow-hidden">
            <div className="flex items-center border-b border-white/[0.07]">
              {[{id:'works',label:`All Works (${works.length})`},{id:'pins',label:`Pinned (${pins.length}/12)`}].map(t => (
                <button key={t.id} type="button" onClick={() => setPinTab(t.id)}
                  className={`flex-1 px-4 py-3 text-sm font-semibold transition-all ${pinTab===t.id ? 'text-[#62FAE3] border-b-2 border-[#62FAE3]' : 'text-[#8a94a6] hover:text-white'}`}>
                  {t.label}
                </button>
              ))}
            </div>
            <div className="p-4 max-h-64 overflow-y-auto space-y-1.5">
              {(pinTab === 'works' ? works : works.filter(w => pins.find(p => p.source_id === w.id))).length === 0 ? (
                <div className="text-center py-8">
                  <Zap className="w-7 h-7 mx-auto mb-2 text-[#62FAE3]/25" />
                  <p className="text-[#8a94a6] text-sm">{pinTab === 'pins' ? 'No pins yet — click works to pin them.' : 'Create a story or project to pin here.'}</p>
                </div>
              ) : (
                (pinTab === 'works' ? works : works.filter(w => pins.find(p => p.source_id === w.id))).map(w => {
                  const pinned = pins.find(p => p.source_id === w.id);
                  return (
                    <div key={w.id} onClick={() => togglePin(w)}
                      className={`cursor-pointer flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl border transition-all
                        ${pinned ? 'border-[#62FAE3]/30 bg-[#62FAE3]/5' : 'border-white/[0.07] hover:border-white/20 hover:bg-white/[0.04]'}`}>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-white font-medium truncate">{w.title||'Untitled'}</p>
                        <p className="text-[10px] text-[#8a94a6] capitalize">{w.source_type}</p>
                      </div>
                      <div className={`flex-shrink-0 ${pinned ? 'text-[#62FAE3]' : 'text-[#8a94a6]'}`}>
                        {pinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* RIGHT sidebar: preview + suggestions + prefs */}
        <div className="hidden lg:flex flex-col gap-4 w-72 xl:w-80 flex-shrink-0 p-5 border-l border-white/[0.06]">
          {/* Live preview */}
          <div>
            <p className="text-[11px] font-bold text-[#8a94a6] uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <Eye className="w-3.5 h-3.5" /> Live Preview
            </p>
            <ProfilePreview displayName={displayName} handle={handle} bio={bio} role={role} avatarUrl={avatarUrl} progress={progress} />
          </div>

          {/* Suggestions */}
          {suggestions.length > 0 && (
            <div className="rounded-2xl border border-white/[0.08] bg-white/[0.02] overflow-hidden">
              <div className="px-4 py-3 border-b border-white/[0.07] flex items-center gap-2">
                <Users className="w-4 h-4 text-[#62FAE3]" />
                <span className="text-sm font-bold text-white">Other Creators</span>
              </div>
              <div className="p-2">
                {suggestions.map(p => (
                  <SuggestCard key={p.id||p.handle} profile={p} onClick={() => navigate(`/network/${p.handle}`)} />
                ))}
              </div>
              <div className="px-4 py-2.5 border-t border-white/[0.07]">
                <button type="button" onClick={() => navigate('/network')}
                  className="text-xs text-[#62FAE3] hover:underline w-full text-center">Browse all creators →</button>
              </div>
            </div>
          )}

          {/* Preferences panel */}
          <div className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-4 space-y-3">
            <p className="text-sm font-bold text-white flex items-center gap-2">
              <SlidersHorizontal className="w-4 h-4 text-[#D0BCFF]" /> Preferences
            </p>
            {[
              { label:'Visible in directory', active:profile?.published, hint: profile?.published ? 'Published' : 'Unpublished' },
              { label:'Accept DM requests', active:accepting, hint: accepting ? 'Open' : 'Closed' },
            ].map(({ label, active, hint }) => (
              <div key={label} className="flex items-center gap-2.5">
                <div className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 transition-all ${active ? 'bg-[#62FAE3]' : 'bg-white/10 border border-white/20'}`}>
                  {active && <Check className="w-3 h-3 text-[#080d14]" strokeWidth={3} />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-[#c5cbd6]">{label}</p>
                </div>
                <span className={`text-[10px] font-semibold ${active ? 'text-[#62FAE3]' : 'text-[#8a94a6]'}`}>{hint}</span>
              </div>
            ))}
            <p className="text-[10px] text-[#8a94a6] pt-1 border-t border-white/[0.07] leading-relaxed">
              Save the form above to apply preference changes.
            </p>
          </div>

          {/* Level guide */}
          <div className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-4 space-y-2">
            <p className="text-sm font-bold text-white flex items-center gap-2">
              <Zap className="w-4 h-4 text-[#62FAE3]" /> Earn XP
            </p>
            {[['Publish profile','50'],['Pin a project','20'],['Story / narration','25'],['Completed job','10']].map(([l,xp]) => (
              <div key={l} className="flex items-center justify-between text-[11px]">
                <span className="text-[#8a94a6]">{l}</span>
                <span className="font-bold text-[#62FAE3]">+{xp} XP</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NetworkProfileEditorPage;
